/**
 * Admin + public Artworks API client.
 * Thin wrapper around axios so calling components stay clean.
 */
import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Admin ────────────────────────────────────────────────────────

export async function listArtworks({ status, artist_id, search } = {}) {
  const params = {};
  if (status && status !== 'all') params.status = status;
  if (artist_id) params.artist_id = artist_id;
  if (search) params.search = search;
  const res = await axios.get(`${BASE}/admin/artworks`, { params });
  return res.data;
}

export async function getArtworksArtistsPicklist() {
  const res = await axios.get(`${BASE}/admin/artworks/artists-picklist`);
  return res.data;
}

export async function getArtwork(id) {
  const res = await axios.get(`${BASE}/admin/artworks/${id}`);
  return res.data;
}

export async function createArtwork(payload) {
  const res = await axios.post(`${BASE}/admin/artworks`, payload);
  return res.data;
}

export async function updateArtwork(id, patch) {
  const res = await axios.patch(`${BASE}/admin/artworks/${id}`, patch);
  return res.data;
}

export async function requestArtworkUploadUrl({ file_name, mime_type, kind = 'media' }) {
  const res = await axios.post(`${BASE}/admin/artworks/upload-url`, {
    file_name, mime_type, kind,
  });
  return res.data;
}

export async function publishArtwork(id) {
  const res = await axios.post(`${BASE}/admin/artworks/${id}/publish`);
  return res.data;
}
export async function hideArtwork(id) {
  const res = await axios.post(`${BASE}/admin/artworks/${id}/hide`);
  return res.data;
}
export async function archiveArtwork(id) {
  const res = await axios.post(`${BASE}/admin/artworks/${id}/archive`);
  return res.data;
}
export async function markSoldOut(id) {
  const res = await axios.post(`${BASE}/admin/artworks/${id}/mark-sold-out`);
  return res.data;
}
export async function markDraft(id) {
  const res = await axios.post(`${BASE}/admin/artworks/${id}/mark-draft`);
  return res.data;
}

export async function createArtworkFromWork(payload) {
  const res = await axios.post(`${BASE}/admin/artworks/from-artist-work`, payload);
  return res.data;
}

export async function getArtworkDependencies(id) {
  const res = await axios.get(`${BASE}/admin/artworks/${id}/dependencies`);
  return res.data;
}

export async function deleteArtworkPermanent(id) {
  const res = await axios.delete(`${BASE}/admin/artworks/${id}/permanent`);
  return res.data;
}

// ─── Offer PDF ────────────────────────────────────────────────────

export async function generateOfferPdf({ artwork_ids, title, currency, private_note }) {
  const res = await axios.post(`${BASE}/admin/artworks/offer-pdf`, {
    artwork_ids, title, currency, private_note,
  });
  return res.data;
}

export async function listOfferDocuments() {
  const res = await axios.get(`${BASE}/admin/artworks/offer-documents`);
  return res.data;
}

// ─── Inquiries ────────────────────────────────────────────────────

export async function listArtworkInquiries({ status, artwork_id, artist_id } = {}) {
  const params = {};
  if (status && status !== 'all') params.status = status;
  if (artwork_id) params.artwork_id = artwork_id;
  if (artist_id) params.artist_id = artist_id;
  const res = await axios.get(`${BASE}/admin/artwork-inquiries`, { params });
  return res.data;
}

export async function updateInquiry(id, patch) {
  const res = await axios.patch(`${BASE}/admin/artwork-inquiries/${id}`, patch);
  return res.data;
}

// ─── Artist website ───────────────────────────────────────────────

export async function publishArtistWebsite(artistId) {
  const res = await axios.post(`${BASE}/admin/artists/${artistId}/publish-website`);
  return res.data;
}
export async function getArtistWebsite(artistId) {
  const res = await axios.get(`${BASE}/admin/artists/${artistId}/website`);
  return res.data;
}
export async function updateArtistPage(pageId, patch) {
  const res = await axios.patch(`${BASE}/admin/artist-pages/${pageId}`, patch);
  return res.data;
}

// ─── Multi-image artwork gallery ─────────────────────────────────

export async function listArtworkImages(artworkId, section) {
  const params = section ? { section } : {};
  const res = await axios.get(`${BASE}/admin/artworks/${artworkId}/images`, { params });
  return res.data;
}

export async function addArtworkImage(artworkId, payload) {
  const res = await axios.post(`${BASE}/admin/artworks/${artworkId}/images`, payload);
  return res.data;
}

export async function updateArtworkImage(artworkId, imageId, patch) {
  const res = await axios.patch(`${BASE}/admin/artworks/${artworkId}/images/${imageId}`, patch);
  return res.data;
}

export async function deleteArtworkImage(artworkId, imageId) {
  const res = await axios.delete(`${BASE}/admin/artworks/${artworkId}/images/${imageId}`);
  return res.data;
}

export async function reorderArtworkImages(artworkId, orderedIds) {
  const res = await axios.post(`${BASE}/admin/artworks/${artworkId}/images/reorder`, { ordered_ids: orderedIds });
  return res.data;
}

// ─── Modular story sections + provenance ─────────────────────────

export async function listArtworkSections(artworkId) {
  const res = await axios.get(`${BASE}/admin/artworks/${artworkId}/sections`);
  return res.data;
}
export async function saveArtworkSections(artworkId, sections) {
  const res = await axios.put(`${BASE}/admin/artworks/${artworkId}/sections`, { sections });
  return res.data;
}
export async function listArtworkProvenance(artworkId) {
  const res = await axios.get(`${BASE}/admin/artworks/${artworkId}/provenance`);
  return res.data;
}
export async function addArtworkProvenance(artworkId, payload) {
  const res = await axios.post(`${BASE}/admin/artworks/${artworkId}/provenance`, payload);
  return res.data;
}
export async function updateArtworkProvenance(artworkId, provId, patch) {
  const res = await axios.patch(`${BASE}/admin/artworks/${artworkId}/provenance/${provId}`, patch);
  return res.data;
}
export async function deleteArtworkProvenance(artworkId, provId) {
  const res = await axios.delete(`${BASE}/admin/artworks/${artworkId}/provenance/${provId}`);
  return res.data;
}

// ─── AI studio ───────────────────────────────────────────────────

export async function aiGenerateImage(artworkId, body) {
  const res = await axios.post(`${BASE}/admin/artworks/${artworkId}/ai/generate-image`, body);
  return res.data;
}
export async function aiGenerateCopy(artworkId, body) {
  const res = await axios.post(`${BASE}/admin/artworks/${artworkId}/ai/generate-copy`, body);
  return res.data;
}
export async function listAiGenerations(artworkId, params) {
  const res = await axios.get(`${BASE}/admin/artworks/${artworkId}/ai/generations`, { params: params || {} });
  return res.data;
}
export async function aiApproveGeneration(artworkId, genId) {
  const res = await axios.post(`${BASE}/admin/artworks/${artworkId}/ai/generations/${genId}/approve`);
  return res.data;
}
export async function aiDiscardGeneration(artworkId, genId) {
  const res = await axios.delete(`${BASE}/admin/artworks/${artworkId}/ai/generations/${genId}`);
  return res.data;
}

// ─── Direct upload to Supabase signed URL ────────────────────────

export async function directUploadToSupabase(signedUrl, file) {
  const clean = axios.create();
  await clean.put(signedUrl, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-upsert': 'true',
    },
  });
}
